<template>
  <img
    ref="imageRef"
    class="transform-gpu rounded-sm"
    :class="[
      {
        'duration-400 transition-opacity ease-linear': loading === 'lazy' && effect === 'fade'
      },
      loading === 'lazy' && effect === 'fade' && 'opacity-0',
      loading === 'lazy' && effect === 'fade' && isVisible && 'opacity-100'
    ]"
    :src="src"
    :srcset="srcset"
    :sizes="sizes"
    :loading="loading && loading"
    :width="width"
    :height="height"
    :alt="alt"
    @load="onLoad($event)"
    @error="onError($event)"
  />
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import type { UseIntersectionObserverOptions } from '@vueuse/core'

export interface Image {
  preload?: boolean
  src: string | undefined
  srcset?: string | undefined
  width: number
  height: number
  alt: string
  sizes?: string
  loading?: 'eager' | 'lazy' | undefined
  effect?: 'fade' | 'none'
  observerOptions?: UseIntersectionObserverOptions
}

const props = withDefaults(defineProps<Image>(), {
  effect: 'fade',
  srcset: undefined,
  sizes: undefined,
  loading: undefined,
  observerOptions: () => ({
    threshold: 0.1
  })
})

const imageRef = ref<HTMLElement | null>(null)
const isLoaded = ref(false)
const isVisible = ref(false)

defineExpose({
  imageRef
})

const emit = defineEmits(['load', 'error'])

function onLoad(e: Event) {
  isLoaded.value = true
  emit('load', e)
}

function onError(e: Event) {
  console.error(e)
  emit('error', e)
}

const { stop } = useIntersectionObserver(
  imageRef,
  ([entry], _observerElement) => {
    if (entry?.isIntersecting && !isVisible.value) {
      isVisible.value = true
    }
  },
  props.observerOptions
)

onUnmounted(() => {
  stop()
})

if (props.preload) {
  useHead({
    link: [
      {
        rel: 'preload',
        fetchpriority: 'high',
        as: 'image',
        imagesrcset: props.srcset ? props.srcset : undefined,
        imagesizes: props.sizes ? props.sizes : undefined,
        href: !props.srcset ? props.src : undefined
      }
    ]
  })
}
</script>
